// LAYOUT-SPEC §0 — Awaiting approval (Deliverables ?awaiting=1[&team=<id>][&duty=<id>])
//
//   ONE QUESTION  Which date proposals are waiting, and on whom?
//   PRIMARY OBJECT  The program's waiver, extension and not-owed proposals
//                   (D111), grouped by step (SAD-866): proposed (awaiting
//                   the COR), COR agreed (awaiting a second approval; a
//                   not-owed mark needs no COR and starts here), approved.
//   PRIMARY VERB    Approve all; the COR's answer for the selected proposed
//                   rows; Export for the client meeting.
//
// Rows are ListOccurrenceDecisions program-wide, the same read the team
// tile, the home Do-next row, the page banners and the date rows' marks use.
// The proposer can never approve their own proposal: the server refuses it,
// and the row mirrors that refusal.
//
// DateWaiversSection is the same rows and actions for deliverable waivers
// only, open and approved, on the Waivers & Tailoring page.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCheckbox, SpecraDateField, SpecraEmptyState, SpecraEntityCell, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraLink, SpecraListPage, SpecraOverlayDialog, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraText, SpecraTextarea, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../../contexts/authContext';
import { useArtifactNames, useUploadArtifacts } from '../../../../hooks/useDeliveryDocuments';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { exportToExcel } from '../../../../lib/utils/exportToExcel';
import { useProgramDocuments } from '../../Docs/hooks';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useCmAccess } from '../access';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useApproveDecision, useAwaitingApproval, useCmInvalidate, useOccurrenceDecisions, useOccurrences, useProposeExtensionEvidence, useProposeWaiverEdit, useProposeWaiverEvidence, useRejectDecision } from '../hooks';
import { currentClinLabel, dutyClinDisplay } from '../lib/dutyClin';
import { sourceDocChipLabel } from '../lib/dutySource';
import { cmDate, instantDay } from '../lib/format';
import { decisionNeedsNoCor, decisionStepLabel, isOccurrenceState, NOT_OWED_COPY, STATE_SHORT_LABEL } from '../lib/status';
import { clinFilterOptions, filterDateRows, fromUiSortDir, normalizeTeamId, resolveOccurrencePeriodId, sortDateRows, toUiSortDir, uniqueSorted } from '../lib/tableControls';
import { useCmLookups, useUserNames } from '../lookups';
import { CorAnswerDrawer } from './CorAnswer';
import { CmDecisionEvidence, CmTable, DutySourceChips, linkLabelFromUrl } from './common';
import { DeliverableFilters } from './DeliverableFilters';
/** The product's names for a proposal's kind. */ export const DECISION_KIND_LABEL = {
    not_owed: 'Not owed (never required)',
    waiver: 'Waiver',
    extension: 'Extension',
    submission_edit: 'Submission edit',
    submission_evidence: 'Add evidence',
    waiver_evidence: 'Add waiver evidence',
    extension_evidence: 'Add extension evidence',
    waiver_edit: 'Waiver edit'
};
/**
 * What a waiver edit changes, one line per field: "COR date: 20 Dec 2024 → 18 Dec 2024".
 * Empty when the decision carries no before (not a waiver edit).
 */ export function waiverEditChanges(d) {
    const b = d.waiverBefore;
    if (!b) return [];
    const out = [];
    if (b.waivedOn !== d.waivedOn) {
        out.push(`COR date: ${cmDate(b.waivedOn, 'not recorded')} → ${cmDate(d.waivedOn, 'not recorded')}`);
    }
    if (b.reason !== d.reason) out.push(`Reason: “${b.reason}” → “${d.reason}”`);
    if (b.waiverDocumentId !== d.waiverDocumentId) {
        out.push(!b.waiverDocumentId ? 'Document added' : !d.waiverDocumentId ? 'Document removed' : 'Document replaced');
    }
    return out;
}
/**
 * A proposal's note in words: an extension names its new date first. The
 * COR's reason is the COR column's (SAD-866), never repeated here.
 */ export function decisionWhy(d) {
    if (d.kind === 'not_owed') return d.note || NOT_OWED_COPY.reasons[d.reason] || d.reason;
    if (d.kind === 'extension') return `To ${cmDate(d.dueOn)}${d.note ? `: ${d.note}` : ''}`;
    if (d.kind === 'submission_edit') {
        const date = d.submittedOn ? `Submitted ${cmDate(d.submittedOn)}` : 'Correct the filing';
        return `${date}${d.note ? `: ${d.note}` : ''}`;
    }
    if (d.kind === 'submission_evidence') return d.note || 'Add evidence';
    if (d.kind === 'waiver_evidence') return d.note || 'Add waiver evidence';
    if (d.kind === 'extension_evidence') return d.note || 'Add extension evidence';
    if (d.kind === 'waiver_edit') {
        const changes = waiverEditChanges({
            waivedOn: d.waivedOn ?? '',
            reason: d.reason,
            waiverDocumentId: d.waiverDocumentId ?? '',
            waiverBefore: d.waiverBefore
        }).join('; ');
        return [
            changes,
            d.note
        ].filter(Boolean).join(' · ');
    }
    return d.note;
}
/** The COR's answer on a waiver or extension, in words; '' when none yet. */ export function corWords(d) {
    if (decisionNeedsNoCor(d.kind)) return '';
    if (!d.reason) return '';
    return `Agreed${d.corAgreedOn ? ` ${cmDate(d.corAgreedOn)}` : ''}: ${d.reason}`;
}
/** The steps the list groups by, in order. */ const GROUPS = [
    {
        step: 'proposed',
        title: 'Proposed: awaiting the COR',
        empty: 'No proposal is waiting on the COR.'
    },
    {
        step: 'awaiting_approval',
        // Neutral: not_owed and submission edit/evidence need no COR (SAD-866, SAD-902).
        title: 'Awaiting a second approval',
        empty: 'Nothing is waiting on a second approval.'
    },
    {
        step: 'approved',
        title: 'Approved',
        empty: 'Nothing approved yet.'
    }
];
export const SELF_PROPOSED_TOOLTIP = 'You proposed this; another person must approve';
/** Deny and withdraw are one click; the server keeps a reason, so this is it. */ export const DENY_REASON = 'No reason given.';
/** How many approvals run at once in a bulk approve. */ const BATCH = 4;
/** Rows a group shows at once; the rest are a page away (SAD-993). */ export const APPROVALS_PAGE_SIZE = 25;
/** The change-set states a proposal is still open in (pangaea's in-flight set). */ export const OPEN_STATES = new Set([
    'Draft',
    'Parsed',
    'UnderReview',
    'Approved'
]);
/** The awaiting-approval list's address, optionally for one team or deliverable. */ export function awaitingHref(programId, { team, duty } = {}) {
    const p = new URLSearchParams({
        awaiting: '1'
    });
    if (team) p.set('team', team);
    if (duty) p.set('duty', duty);
    return `/delivery/programs/${programId}/deliverables?${p.toString()}`;
}
/**
 * "n dates awaiting approval — Review" on a page whose dates carry open
 * proposals: the whole program, one team, or one deliverable.
 */ export function AwaitingApprovalBanner({ programId, team, duty }) {
    const { query, occById } = useAwaitingApproval(programId);
    const n = (query.data?.decisions ?? []).filter((d)=>{
        const o = occById.get(d.occurrenceId);
        return (!team || o?.workstreamId === team) && (!duty || o?.dutyId === duty);
    }).length;
    if (n === 0) return null;
    return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "warning",
        variant: "framed",
        "data-testid": Testids.cmApprovals.banner,
        action: /*#__PURE__*/ _jsx(SpecraLink, {
            href: awaitingHref(programId, {
                team,
                duty
            }),
            renderLink: renderRouterLink,
            "data-testid": Testids.cmApprovals.bannerReview,
            children: "Review"
        }),
        children: [
            n,
            " ",
            n === 1 ? 'date' : 'dates',
            " awaiting approval"
        ]
    });
}
function toRows(decisions, occById, lk, userId, clins) {
    return decisions.map((decision)=>{
        const occ = occById.get(decision.occurrenceId);
        const d = occ ? lk.dutyById.get(occ.dutyId) : undefined;
        const dutyClin = dutyClinDisplay(d?.dutyKey ?? '', clins);
        const exerciseClin = occ ? lk.exerciseById.get(occ.exerciseId)?.clinNumber : undefined;
        const clinLabel = dutyClin.label || exerciseClin || '';
        const teamId = normalizeTeamId(occ?.workstreamId);
        const periodId = occ ? resolveOccurrencePeriodId(occ, lk.periods ?? [], lk.exercises ?? []) : '';
        return {
            decision,
            occ,
            title: d?.title || 'Deliverable',
            ref: [
                d?.letter,
                clinLabel ? `CLIN ${clinLabel}` : ''
            ].filter(Boolean).join(' · '),
            team: lk.teamName(teamId),
            mine: !!userId && decision.proposedByUserId === userId,
            open: OPEN_STATES.has(decision.state),
            step: decision.step,
            dutyKey: d?.dutyKey ?? '',
            pwsRef: d?.pwsRef ?? '',
            letter: d?.letter ?? '',
            origin: d?.origin ?? 'award',
            sourceDocumentId: d?.sourceDocumentId ?? '',
            clinLabel,
            teamId,
            dueOn: occ?.dueOn ?? '',
            statusState: occ?.status?.state ?? '',
            periodId
        };
    });
}
const rowName = (r)=>`${r.title}${r.occ ? ` due ${cmDate(r.occ.dueOn)}` : ''}`;
/**
 * Approve, deny and approve-many over decision rows. Each row is its own
 * approval: in a batch, one failing names itself and the rest still run.
 */ function useApprovalRunner(programId, onApproved) {
    const approveOne = useApproveDecision(programId, {
        success: 'Approved'
    });
    // A bulk run refreshes once when it ends, not once per approved row.
    const approveMany = useApproveDecision(programId, {
        errors: 'inline',
        deferRefresh: true
    });
    const refresh = useCmInvalidate(programId);
    const deny = useRejectDecision(programId);
    const [busy, setBusy] = useState(new Set());
    const [failures, setFailures] = useState([]);
    const markBusy = (ids, on)=>setBusy((prev)=>{
            const next = new Set(prev);
            for (const id of ids){
                if (on) next.add(id);
                else next.delete(id);
            }
            return next;
        });
    const approveRow = (r)=>{
        const id = r.decision.changeSetId;
        markBusy([
            id
        ], true);
        approveOne.mutate({
            changeSetId: id
        }, {
            onSettled: ()=>markBusy([
                    id
                ], false)
        });
    };
    const denyRow = (r)=>{
        const id = r.decision.changeSetId;
        markBusy([
            id
        ], true);
        deny.mutate({
            changeSetId: id,
            reason: DENY_REASON
        }, {
            onSuccess: ()=>toast.success(r.mine ? 'Proposal withdrawn' : 'Proposal denied'),
            onSettled: ()=>markBusy([
                    id
                ], false)
        });
    };
    const approveRows = async (todo)=>{
        setFailures([]);
        markBusy(todo.map((r)=>r.decision.changeSetId), true);
        const failed = [];
        for(let i = 0; i < todo.length; i += BATCH){
            await Promise.all(todo.slice(i, i + BATCH).map(async (r)=>{
                const id = r.decision.changeSetId;
                try {
                    await approveMany.mutateAsync({
                        changeSetId: id
                    });
                    onApproved?.(id);
                } catch (err) {
                    failed.push({
                        name: rowName(r),
                        error: apiErrorMessage(err, 'Could not approve')
                    });
                } finally{
                    markBusy([
                        id
                    ], false);
                }
            }));
        }
        setFailures(failed);
        const ok = todo.length - failed.length;
        if (ok > 0) await refresh('approveOccurrenceDecision');
        if (failed.length === 0) toast.success(`Approved ${ok}`);
        else toast.error(`Approved ${ok} of ${todo.length}; ${failed.length} could not be approved`);
    };
    return {
        busy,
        running: busy.size > 0,
        failures,
        clearFailures: ()=>setFailures([]),
        approveRow,
        denyRow,
        approveRows
    };
}
function isSubmissionDecision(kind) {
    return kind === 'submission_edit' || kind === 'submission_evidence' || kind === 'waiver_evidence' || kind === 'extension_evidence' || kind === 'waiver_edit';
}
/**
 * Approve (disabled on your own, with the reason) and Deny / Withdraw.
 * Waiver-style one-click Deny stays for COR proposals; submission edit /
 * evidence asks for a short reason (SAD-917). A proposal awaiting the COR
 * offers the COR's answer instead of Approve (SAD-866).
 */ function DecisionActions({ r, runner, onRecordCor, onDenyWithReason }) {
    const approve = r.step === 'proposed' ? /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        disabled: runner.running,
        onClick: ()=>onRecordCor?.(r),
        "data-testid": Testids.cmApprovals.recordCor,
        children: "Record the COR’s answer"
    }) : /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        disabled: r.mine || runner.running,
        isLoading: runner.busy.has(r.decision.changeSetId),
        onClick: ()=>runner.approveRow(r),
        "data-testid": Testids.cmApprovals.approve,
        children: "Approve"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex justify-end gap-2",
        children: [
            r.mine && r.step !== 'proposed' ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                content: SELF_PROPOSED_TOOLTIP,
                children: /*#__PURE__*/ _jsx("span", {
                    children: approve
                })
            }) : approve,
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                disabled: runner.running,
                onClick: ()=>isSubmissionDecision(r.decision.kind) && onDenyWithReason ? onDenyWithReason(r) : runner.denyRow(r),
                "data-testid": Testids.cmApprovals.reject,
                children: r.mine ? 'Withdraw' : 'Deny'
            })
        ]
    });
}
/** SAD-917: deny / withdraw a submission edit or evidence with a typed reason. */ function DenyReasonDialog({ programId, row, onClose }) {
    const withdraw = row.mine;
    const reject = useRejectDecision(programId, {
        errors: 'inline',
        success: withdraw ? 'Proposal withdrawn' : 'Proposal rejected'
    });
    const [reason, setReason] = useState('');
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        size: "sm",
        title: withdraw ? 'Withdraw your proposal' : 'Reject this proposal',
        testId: Testids.cmApprovals.rejectDialog,
        footerProps: {
            confirmLabel: withdraw ? 'Withdraw' : 'Reject',
            confirmDisabled: reason.trim() === '' || reject.isPending,
            onCancel: onClose,
            onConfirm: ()=>{
                reject.mutate({
                    changeSetId: row.decision.changeSetId,
                    reason: reason.trim()
                }, {
                    onSuccess: onClose
                });
            },
            confirmProps: {
                'data-testid': Testids.cmApprovals.rejectConfirm
            }
        },
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Reason",
                required: true,
                hint: withdraw ? 'Kept in the history. The filing is unchanged.' : 'The proposer sees this. The filing is unchanged and can be proposed again.',
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    rows: 2,
                    value: reason,
                    onChange: (e)=>setReason(e.target.value),
                    "data-testid": Testids.cmApprovals.rejectReason
                })
            }),
            reject.error ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "danger",
                className: "mt-2",
                children: apiErrorMessage(reject.error, 'Could not reject')
            }) : null
        ]
    });
}
function ApprovalFailures({ runner }) {
    if (runner.failures.length === 0) return null;
    return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        tone: "danger",
        variant: "framed",
        title: `${runner.failures.length} could not be approved`,
        onDismiss: runner.clearFailures,
        "data-testid": Testids.cmApprovals.failures,
        children: /*#__PURE__*/ _jsx("ul", {
            className: "list-disc pl-5",
            children: runner.failures.map((f)=>/*#__PURE__*/ _jsxs("li", {
                    children: [
                        f.name,
                        ": ",
                        f.error
                    ]
                }, f.name))
        })
    });
}
/** [Approve all (n)] and its confirmation; your own proposals are left out. */ function ApproveAll({ approvable, own, runner }) {
    const [confirming, setConfirming] = useState(false);
    const n = approvable.length;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraButton, {
                disabled: n === 0 || runner.running,
                onClick: ()=>setConfirming(true),
                "data-testid": Testids.cmApprovals.approveAll,
                children: [
                    "Approve all (",
                    n,
                    ")"
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: confirming,
                onOpenChange: setConfirming,
                size: "sm",
                title: `Approve all ${n} ${n === 1 ? 'proposal' : 'proposals'}?`,
                testId: Testids.cmApprovals.approveAllDialog,
                footerProps: {
                    confirmLabel: `Approve all (${n})`,
                    onCancel: ()=>setConfirming(false),
                    onConfirm: ()=>{
                        setConfirming(false);
                        void runner.approveRows(approvable);
                    },
                    confirmProps: {
                        'data-testid': Testids.cmApprovals.approveAllConfirm
                    }
                },
                children: /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "body-ui",
                    children: [
                        "Each takes effect on its date now.",
                        own > 0 ? ` ${own} you proposed ${own === 1 ? 'is' : 'are'} left for someone else.` : ''
                    ]
                })
            })
        ]
    });
}
function baseColumns(names, programId, clins, artifactNames) {
    return [
        {
            id: 'title',
            header: 'Deliverable',
            sortable: true,
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraEntityCell, {
                            title: r.title,
                            description: r.ref || undefined
                        }),
                        /*#__PURE__*/ _jsx(DutySourceChips, {
                            programId: programId,
                            origin: r.origin,
                            pwsRef: r.pwsRef,
                            letter: r.letter,
                            dutyKey: r.dutyKey,
                            sourceDocumentId: r.sourceDocumentId,
                            clins: clins,
                            testIds: {
                                pws: Testids.cmApprovals.sourcePws,
                                clin: Testids.cmApprovals.sourceClin,
                                sourceDoc: Testids.cmApprovals.sourceDoc
                            }
                        })
                    ]
                })
        },
        {
            id: 'team',
            header: 'Team',
            sortable: true,
            cell: (r)=>r.team
        },
        {
            id: 'due',
            header: 'Due',
            sortable: true,
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: cmDate(r.occ?.dueOn, '—')
                })
        },
        {
            id: 'by',
            header: 'Proposed by',
            cell: (r)=>names(r.decision.proposedByUserId)
        },
        {
            id: 'on',
            header: 'Proposed on',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: cmDate(instantDay(r.decision.proposedAt), '—')
                })
        },
        {
            id: 'reason',
            header: 'Note',
            cell: (r)=>{
                const d = r.decision;
                const why = decisionWhy(d);
                return /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: why || '—'
                });
            }
        },
        {
            id: 'cor',
            header: 'COR',
            cell: (r)=>{
                if (decisionNeedsNoCor(r.decision.kind)) {
                    return /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground",
                        children: "—"
                    });
                }
                return /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: corWords(r.decision) || 'Not yet answered'
                });
            }
        },
        {
            id: 'evidence',
            header: 'Evidence',
            cell: (r)=>{
                const evidence = /*#__PURE__*/ _jsx(CmDecisionEvidence, {
                    programId: programId,
                    evidenceDocumentId: r.decision.evidenceDocumentId,
                    attachments: r.decision.attachments,
                    testId: Testids.cmApprovals.evidence,
                    artifactNames: artifactNames
                });
                return evidence ?? /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                });
            }
        }
    ];
}
/** The list as a spreadsheet, for the client meeting. */ function exportRows(rows, names) {
    void exportToExcel({
        filename: 'date-proposals',
        sheetName: 'Proposals',
        data: rows,
        columns: [
            {
                header: 'Step',
                accessor: (r)=>decisionStepLabel(r.decision)
            },
            {
                header: 'Kind',
                accessor: (r)=>DECISION_KIND_LABEL[r.decision.kind] ?? r.decision.kind
            },
            {
                header: 'Deliverable',
                accessor: (r)=>r.title
            },
            {
                header: 'Reference',
                accessor: (r)=>r.ref
            },
            {
                header: 'Team',
                accessor: (r)=>r.team
            },
            {
                header: 'Due',
                accessor: (r)=>r.occ?.dueOn ?? ''
            },
            {
                header: 'Date status',
                accessor: (r)=>r.occ?.status?.state ?? ''
            },
            {
                header: 'New due date',
                accessor: (r)=>r.decision.kind === 'extension' ? r.decision.dueOn : ''
            },
            {
                header: 'Proposed by',
                accessor: (r)=>r.decision.proposedByName || names(r.decision.proposedByUserId)
            },
            {
                header: 'Proposed on',
                accessor: (r)=>instantDay(r.decision.proposedAt)
            },
            {
                header: 'Note',
                accessor: (r)=>r.decision.kind === 'not_owed' ? decisionWhy(r.decision) : r.decision.note
            },
            {
                header: 'COR agreed on',
                accessor: (r)=>r.decision.corAgreedOn
            },
            {
                header: 'COR reason',
                accessor: (r)=>r.decision.kind === 'not_owed' ? '' : r.decision.reason
            }
        ]
    });
}
export function AwaitingApprovalView({ programId }) {
    const [sp, setSp] = useSearchParams();
    const team = sp.get('team') ?? '';
    const duty = sp.get('duty') ?? '';
    const query = sp.get('q') ?? '';
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const docs = useProgramDocuments(programId);
    const names = useUserNames();
    const access = useCmAccess(programId);
    const drawer = useCmDrawerParams();
    const { user } = useAuth();
    // Every state: the approved group is part of the list (SAD-866).
    const q = useOccurrenceDecisions(programId, {});
    const [selected, setSelected] = useState(new Set());
    const [answering, setAnswering] = useState(null);
    const [denying, setDenying] = useState(null);
    const [clinFilter, setClinFilter] = useState('');
    const [pwsFilter, setPwsFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceDocFilter, setSourceDocFilter] = useState('');
    const [periodFilter, setPeriodFilter] = useState('');
    const [sort, setSort] = useState(null);
    const toggle = (id, on)=>setSelected((prev)=>{
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });
    const runner = useApprovalRunner(programId, (id)=>toggle(id, false));
    const allRows = useMemo(()=>{
        const occById = new Map((q.data?.occurrences ?? []).map((o)=>[
                o.id,
                o
            ]));
        return toRows(q.data?.decisions ?? [], occById, lk, user?.id, clins).filter((r)=>GROUPS.some((g)=>g.step === r.step) && (!duty || r.occ?.dutyId === duty));
    }, [
        q.data,
        lk,
        user?.id,
        duty,
        clins
    ]);
    const rows = useMemo(()=>{
        const control = allRows.map((r)=>({
                ...r,
                teamName: r.team,
                clinLabel: r.clinLabel
            }));
        const filtered = filterDateRows(control, {
            q: query,
            clin: clinFilter,
            team,
            status: statusFilter,
            period: periodFilter,
            sourceDoc: sourceDocFilter,
            pws: pwsFilter
        }, clins);
        const sorted = sortDateRows(filtered, sort);
        const byId = new Map(allRows.map((r)=>[
                r.decision.changeSetId,
                r
            ]));
        return sorted.flatMap((r)=>{
            const row = byId.get(r.decision.changeSetId);
            return row ? [
                row
            ] : [];
        });
    }, [
        allRows,
        query,
        clinFilter,
        team,
        statusFilter,
        periodFilter,
        sourceDocFilter,
        pwsFilter,
        sort,
        clins
    ]);
    const filterOptions = useMemo(()=>{
        const docById = new Map((docs.data?.items ?? []).map((d)=>[
                d.id,
                d
            ]));
        return {
            clins: clinFilterOptions(allRows.map((r)=>r.dutyKey), clins).map((v)=>({
                    value: v,
                    label: v
                })),
            pws: uniqueSorted(allRows.map((r)=>r.pwsRef)).map((v)=>({
                    value: v,
                    label: `PWS ${v}`
                })),
            teams: (lk.workstreams ?? []).map((w)=>({
                    value: w.id,
                    label: w.name
                })),
            statuses: uniqueSorted(allRows.map((r)=>r.statusState)).filter((s)=>isOccurrenceState(s)).map((s)=>({
                    value: s,
                    label: STATE_SHORT_LABEL[s]
                })),
            sourceDocs: uniqueSorted(allRows.map((r)=>r.sourceDocumentId).filter(Boolean)).map((id)=>{
                const d = docById.get(id);
                return {
                    value: id,
                    label: sourceDocChipLabel(d ? {
                        id,
                        title: d.title,
                        fileName: d.fileName,
                        category: d.category
                    } : {
                        id
                    }, 'award')
                };
            }),
            periods: (lk.periods ?? []).map((p)=>({
                    value: p.id,
                    label: p.name
                }))
        };
    }, [
        allRows,
        clins,
        docs.data,
        lk.workstreams,
        lk.periods
    ]);
    const byStep = (step)=>rows.filter((r)=>r.step === step);
    // Each group shows a page of rows; the history behind "Approved" grows
    // without bound, so its files are named a page at a time.
    const [pages, setPages] = useState({});
    const pageCountOf = (step)=>Math.max(1, Math.ceil(byStep(step).length / APPROVALS_PAGE_SIZE));
    const pageOf = (step)=>Math.min(pages[step] ?? 1, pageCountOf(step));
    const shownIn = (step)=>{
        const start = (pageOf(step) - 1) * APPROVALS_PAGE_SIZE;
        return byStep(step).slice(start, start + APPROVALS_PAGE_SIZE);
    };
    const shownArtifactIds = GROUPS.flatMap((g)=>shownIn(g.step)).flatMap((r)=>r.decision.attachments.map((a)=>a.artifactId));
    const artifactNames = useArtifactNames(shownArtifactIds);
    const canAct = access.canWrite;
    const awaiting = byStep('awaiting_approval');
    const approvable = awaiting.filter((r)=>!r.mine);
    const proposed = byStep('proposed');
    // Selection survives a refetch only for rows still listed and selectable.
    const pickedApprove = approvable.filter((r)=>selected.has(r.decision.changeSetId));
    const pickedCor = proposed.filter((r)=>selected.has(r.decision.changeSetId));
    const openCount = proposed.length + awaiting.length;
    const setParam = (key, value)=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            if (value) n.set(key, value);
            else n.delete(key);
            return n;
        }, {
            replace: true
        });
    const base = baseColumns(names, programId, clins, artifactNames);
    const columnsFor = (step, selectable)=>{
        const all = selectable.length > 0 && selectable.every((r)=>selected.has(r.decision.changeSetId));
        const some = selectable.some((r)=>selected.has(r.decision.changeSetId));
        const canSelect = (r)=>selectable.includes(r);
        return [
            ...canAct && step !== 'approved' ? [
                {
                    id: 'select',
                    header: /*#__PURE__*/ _jsx(SpecraCheckbox, {
                        "aria-label": step === 'proposed' ? 'Select every proposal awaiting the COR' : 'Select every proposal you can approve',
                        checked: all ? true : some ? 'indeterminate' : false,
                        disabled: selectable.length === 0 || runner.running,
                        onCheckedChange: (v)=>setSelected((prev)=>{
                                const next = new Set(prev);
                                for (const r of selectable){
                                    if (v === true) next.add(r.decision.changeSetId);
                                    else next.delete(r.decision.changeSetId);
                                }
                                return next;
                            }),
                        "data-testid": Testids.cmApprovals.selectAll
                    }),
                    cell: (r)=>/*#__PURE__*/ _jsx(SpecraCheckbox, {
                            "aria-label": `Select ${rowName(r)}`,
                            checked: canSelect(r) && selected.has(r.decision.changeSetId),
                            disabled: !canSelect(r) || runner.running,
                            onCheckedChange: (v)=>toggle(r.decision.changeSetId, v === true),
                            "data-testid": Testids.cmApprovals.select
                        })
                }
            ] : [],
            ...base.slice(0, 3),
            {
                id: 'kind',
                header: 'Kind',
                cell: (r)=>/*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.cmApprovals.step,
                        "data-step": r.step,
                        children: r.decision.kind === 'submission_edit' || r.decision.kind === 'submission_evidence' ? decisionStepLabel(r.decision) : DECISION_KIND_LABEL[r.decision.kind] ?? 'Proposal'
                    })
            },
            ...base.slice(3).filter((c)=>step !== 'proposed' || c.id !== 'cor')
        ];
    };
    return /*#__PURE__*/ _jsxs(SpecraListPage, {
        "data-testid": Testids.cmApprovals.page,
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            eyebrow: /*#__PURE__*/ _jsx(SpecraLink, {
                href: `/delivery/programs/${programId}/deliverables`,
                renderLink: renderRouterLink,
                children: "Deliverables"
            }),
            title: "Awaiting approval",
            meta: q.data ? openCount : undefined,
            description: "Waivers and extensions are proposed, then the COR answers, then a second person approves. A not-owed mark, a submission edit, and added evidence need no COR. Whoever proposed one cannot approve it.",
            actions: /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "ghost",
                        disabled: rows.length === 0,
                        onClick: ()=>exportRows(rows, names),
                        "data-testid": Testids.cmApprovals.export,
                        children: "Export"
                    }),
                    canAct && pickedCor.length > 0 ? /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "secondary",
                        disabled: runner.running,
                        onClick: ()=>setAnswering(pickedCor),
                        "data-testid": Testids.cmApprovals.recordCorSelected,
                        children: [
                            "Record the COR’s answer (",
                            pickedCor.length,
                            ")"
                        ]
                    }) : null,
                    canAct && pickedApprove.length > 0 ? /*#__PURE__*/ _jsxs(SpecraButton, {
                        variant: "secondary",
                        disabled: runner.running,
                        onClick: ()=>void runner.approveRows(pickedApprove),
                        "data-testid": Testids.cmApprovals.approveSelected,
                        children: [
                            "Approve selected (",
                            pickedApprove.length,
                            ")"
                        ]
                    }) : null,
                    canAct ? /*#__PURE__*/ _jsx(ApproveAll, {
                        approvable: approvable,
                        own: awaiting.length - approvable.length,
                        runner: runner
                    }) : null
                ]
            })
        }),
        toolbarProps: {
            search: {
                value: query,
                onChange: (e)=>setParam('q', e.target.value),
                onClear: ()=>setParam('q', ''),
                placeholder: 'Search proposals…',
                'data-testid': Testids.cmApprovals.search
            },
            filters: /*#__PURE__*/ _jsx(DeliverableFilters, {
                values: {
                    clin: clinFilter,
                    pws: pwsFilter,
                    team,
                    status: statusFilter,
                    sourceDoc: sourceDocFilter,
                    period: periodFilter
                },
                onChange: (key, value)=>{
                    if (key === 'team') setParam('team', value);
                    else if (key === 'clin') setClinFilter(value);
                    else if (key === 'pws') setPwsFilter(value);
                    else if (key === 'status') setStatusFilter(value);
                    else if (key === 'sourceDoc') setSourceDocFilter(value);
                    else if (key === 'period') setPeriodFilter(value);
                },
                options: filterOptions,
                contractWideTeam: false,
                size: "default",
                testIds: {
                    clin: Testids.cmApprovals.clinFilter,
                    pws: Testids.cmApprovals.pwsFilter,
                    team: Testids.cmApprovals.teamFilter,
                    status: Testids.cmApprovals.statusFilter,
                    sourceDoc: Testids.cmApprovals.sourceDocFilter,
                    period: Testids.cmApprovals.periodFilter
                }
            }),
            actions: duty ? /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "ghost",
                onClick: ()=>setParam('duty', ''),
                children: [
                    lk.dutyById.get(duty)?.title ?? 'One deliverable',
                    " · Show all"
                ]
            }) : undefined,
            resultSummary: `${proposed.length} awaiting the COR · ${awaiting.length} awaiting approval`
        },
        children: [
            /*#__PURE__*/ _jsx(ApprovalFailures, {
                runner: runner
            }),
            !q.isLoading && !lk.isLoading && rows.length === 0 && !q.error ? /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.cmApprovals.empty,
                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    framed: true,
                    title: "Nothing is awaiting approval.",
                    description: "Proposals appear here when someone proposes a waiver or an extension of a date, marks one not owed, edits a submission, or adds evidence."
                })
            }) : GROUPS.map((g)=>{
                const groupRows = byStep(g.step);
                const pageCount = pageCountOf(g.step);
                return /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-2",
                    "data-testid": Testids.cmApprovals.group,
                    "data-step": g.step,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: `${g.title} · ${groupRows.length}`
                        }),
                        /*#__PURE__*/ _jsx(CmTable, {
                            caption: g.title,
                            rows: shownIn(g.step),
                            pagination: pageCount > 1 ? {
                                page: pageOf(g.step),
                                pageCount,
                                itemLabel: 'proposals',
                                onPageChange: (page)=>setPages((prev)=>({
                                            ...prev,
                                            [g.step]: page
                                        }))
                            } : undefined,
                            columns: columnsFor(g.step, g.step === 'proposed' ? proposed : approvable),
                            rowKey: (r)=>r.decision.changeSetId,
                            rowTestId: Testids.cmApprovals.row,
                            isLoading: q.isLoading || lk.isLoading,
                            error: q.error,
                            onRetry: ()=>void q.refetch(),
                            onRowClick: (r)=>drawer.open('occ', r.decision.occurrenceId),
                            getRowLabel: (r)=>`Open ${rowName(r)}`,
                            sortColumn: sort?.key,
                            sortDirection: sort ? toUiSortDir(sort.dir) : undefined,
                            onSortChange: (columnId, direction)=>setSort({
                                    key: columnId,
                                    dir: fromUiSortDir(direction)
                                }),
                            rowActions: canAct && g.step !== 'approved' ? (r)=>/*#__PURE__*/ _jsx(DecisionActions, {
                                    r: r,
                                    runner: runner,
                                    onRecordCor: (row)=>setAnswering([
                                            row
                                        ]),
                                    onDenyWithReason: setDenying
                                }) : undefined,
                            empty: /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: g.empty
                            })
                        })
                    ]
                }, g.step);
            }),
            answering ? /*#__PURE__*/ _jsx(CorAnswerDrawer, {
                programId: programId,
                targets: answering.map((r)=>({
                        decision: r.decision,
                        name: rowName(r)
                    })),
                onClose: ()=>setAnswering(null),
                onDone: ()=>{
                    for (const r of answering)toggle(r.decision.changeSetId, false);
                    setAnswering(null);
                }
            }) : null,
            denying ? /*#__PURE__*/ _jsx(DenyReasonDialog, {
                programId: programId,
                row: denying,
                onClose: ()=>setDenying(null)
            }) : null
        ]
    });
}
/**
 * SAD-1010 / SAD-1324: a second person's approval adds files or a link to an approved
 * waiver or extension (the row's own kind).
 */ function EvidenceDialog({ programId, row, onClose }) {
    const what = row.decision.kind === 'extension' ? 'extension' : 'waiver';
    const opts = {
        errors: 'inline',
        success: 'Evidence proposed — awaiting a second person’s approval.'
    };
    const proposeWaiver = useProposeWaiverEvidence(programId, opts);
    const proposeExtension = useProposeExtensionEvidence(programId, opts);
    const propose = what === 'extension' ? proposeExtension : proposeWaiver;
    const upload = useUploadArtifacts();
    const [url, setUrl] = useState('');
    const [note, setNote] = useState('');
    const [uploaded, setUploaded] = useState([]);
    const [uploadError, setUploadError] = useState(null);
    const busy = propose.isPending || upload.isPending;
    const ready = note.trim() !== '' && (!!url.trim() || uploaded.length > 0);
    const error = propose.error ?? uploadError;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        size: "sm",
        title: `Add evidence to this ${what}`,
        testId: Testids.cmApprovals.waiverEvidenceDialog,
        footerProps: {
            confirmLabel: 'Propose evidence',
            confirmDisabled: !ready || busy,
            onCancel: onClose,
            onConfirm: ()=>{
                const attachments = [
                    ...uploaded,
                    ...url.trim() ? [
                        {
                            url: url.trim(),
                            label: linkLabelFromUrl(url.trim())
                        }
                    ] : []
                ];
                propose.mutate({
                    occurrenceId: row.decision.occurrenceId,
                    note: note.trim(),
                    attachments
                }, {
                    onSuccess: onClose
                });
            },
            confirmProps: {
                'data-testid': Testids.cmApprovals.waiverEvidenceConfirm
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: [
                        "The files or link are added to this ",
                        what,
                        " only after a second person approves. The ",
                        what,
                        "stands as it is until then."
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Link",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: url,
                        onChange: (e)=>setUrl(e.target.value),
                        placeholder: "https://",
                        "data-testid": Testids.cmApprovals.waiverEvidenceLink
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Or upload files",
                    children: /*#__PURE__*/ _jsx("input", {
                        type: "file",
                        "data-testid": Testids.cmApprovals.waiverEvidenceFile,
                        multiple: true,
                        onChange: async (e)=>{
                            const picked = Array.from(e.target.files ?? []);
                            if (picked.length === 0) return;
                            setUploadError(null);
                            try {
                                const up = await upload.mutateAsync({
                                    files: picked
                                });
                                setUploaded(up.files.map((a)=>({
                                        artifactId: a.artifactId,
                                        label: a.name
                                    })));
                                const why = up.failed[0];
                                if (why) setUploadError(new Error(`Could not upload ${why.name}: ${why.error}`));
                                else if (up.files.length === 0) setUploadError(new Error('Upload finished with no file — try again.'));
                            } catch (err) {
                                setUploaded([]);
                                setUploadError(err);
                            }
                        }
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why add this",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        "data-testid": Testids.cmApprovals.waiverEvidenceNote
                    })
                }),
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(error, 'Could not propose evidence')
                }) : null
            ]
        })
    });
}
/** Change an approved waiver's COR date, reason or document; a second person approves. */ function WaiverEditDialog({ programId, row, current, onClose }) {
    const propose = useProposeWaiverEdit(programId, {
        errors: 'inline',
        success: 'Waiver edit proposed — awaiting a second person’s approval.'
    });
    const docs = useProgramDocuments(programId);
    const [waivedOn, setWaivedOn] = useState(current.waivedOn);
    const [reason, setReason] = useState(current.reason);
    const [documentId, setDocumentId] = useState(current.waiverDocumentId);
    const [note, setNote] = useState('');
    const changes = {
        ...waivedOn !== current.waivedOn ? {
            waivedOn
        } : {},
        ...reason.trim() !== current.reason ? {
            reason: reason.trim()
        } : {},
        ...documentId !== current.waiverDocumentId ? {
            waiverDocumentId: documentId
        } : {}
    };
    const changed = Object.keys(changes).length > 0;
    const ready = changed && reason.trim() !== '' && note.trim() !== '' && !propose.isPending;
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        onOpenChange: (open)=>{
            if (!open) onClose();
        },
        size: "sm",
        title: "Edit this waiver",
        testId: Testids.cmApprovals.waiverEditDialog,
        footerProps: {
            confirmLabel: 'Propose edit',
            confirmDisabled: !ready,
            onCancel: onClose,
            onConfirm: ()=>propose.mutate({
                    occurrenceId: row.decision.occurrenceId,
                    note: note.trim(),
                    ...changes
                }, {
                    onSuccess: onClose
                }),
            confirmProps: {
                'data-testid': Testids.cmApprovals.waiverEditConfirm
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: "The waiver changes only after a second person approves. Until then it stands as it is."
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "COR date",
                    value: waivedOn,
                    onValueChange: setWaivedOn,
                    "data-testid": Testids.cmApprovals.waiverEditWaivedOn
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "COR's reason",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        "data-testid": Testids.cmApprovals.waiverEditReason
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Waiver document",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Waiver document",
                        value: documentId,
                        onChange: (e)=>setDocumentId(e.target.value),
                        "data-testid": Testids.cmApprovals.waiverEditDocument,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "None"
                            }),
                            (docs.data?.items ?? []).map((d)=>/*#__PURE__*/ _jsx("option", {
                                    value: d.id,
                                    children: d.title || d.fileName
                                }, d.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why change it",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: note,
                        onChange: (e)=>setNote(e.target.value),
                        "data-testid": Testids.cmApprovals.waiverEditNote
                    })
                }),
                !changed ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    children: "Change at least one field."
                }) : null,
                propose.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(propose.error, 'Could not propose the edit')
                }) : null
            ]
        })
    });
}
export const isDateWaiver = (d)=>(d.kind === 'waiver' || d.kind === 'extension') && (d.state === 'Applied' || OPEN_STATES.has(d.state));
const PENDING_STATUS = 'pending';
/**
 * The waivers list's status filter, shared with the Program Home waiver tiles so a
 * tile's count is exactly what its link lists. "pending" is any not-yet-approved step.
 */ export const waiverStatusMatches = (step, status)=>status === PENDING_STATUS ? step === 'proposed' || step === 'awaiting_approval' : step === status;
/** How many deliverable waiver / extension rows DateWaiversSection lists (same cached read). */ export function useDateWaiverCount(programId) {
    const q = useOccurrenceDecisions(programId, {});
    return {
        isLoading: q.isLoading,
        count: (q.data?.decisions ?? []).filter(isDateWaiver).length
    };
}
const STEP_RANK = {
    proposed: 0,
    awaiting_approval: 1,
    approved: 2
};
const WAIVER_STEPS = [
    {
        value: PENDING_STATUS,
        label: 'Pending (not yet approved)'
    },
    {
        value: 'proposed',
        label: 'Awaiting the COR'
    },
    {
        value: 'awaiting_approval',
        label: 'Awaiting approval'
    },
    {
        value: 'approved',
        label: 'Approved'
    }
];
function waiverSortValue(r, key) {
    switch(key){
        case 'title':
            return r.title.toLowerCase();
        case 'team':
            return r.team.toLowerCase();
        case 'due':
            return r.dueOn;
        case 'on':
            return instantDay(r.decision.proposedAt) ?? '';
        case 'corDate':
            return r.step === 'proposed' ? '' : r.decision.corAgreedOn;
        case 'status':
            return STEP_RANK[r.step] ?? 9;
    }
}
/** Empty values sort last in either direction; ISO dates compare chronologically. */ function sortWaiverRows(rows, sort) {
    if (!sort) return rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [
        ...rows
    ].sort((a, b)=>{
        const x = waiverSortValue(a, sort.key);
        const y = waiverSortValue(b, sort.key);
        if (x === y) return 0;
        if (x === '') return 1;
        if (y === '') return -1;
        return (x < y ? -1 : 1) * sign;
    });
}
// Pinned widths of the Deliverable and Team columns; the filter row's grid tracks reuse them.
const TITLE_COL = '18rem';
const TEAM_COL = '13rem';
const pinned = (w)=>({
        width: w,
        minWidth: w,
        maxWidth: w
    });
// specra-allow arbitrary-length: the filter grid tracks must equal the pinned column widths above
const FILTER_ROW = 'flex flex-col gap-2 sm:grid sm:grid-cols-[18rem_13rem_1fr_13rem] sm:gap-0';
// Over a column, a select starts at the column's edge and stops short by the cell padding, so
// neighbours do not touch.
// specra-allow arbitrary-length: one cell padding (--space-2) off the column width
const COLUMN_SELECT = 'w-full sm:w-[calc(100%-var(--space-2))]';
/**
 * Deliverable waivers, open proposals and approved ones,
 * for the Waivers & Tailoring page: approve one, approve all, or deny.
 */ export function DateWaiversSection({ programId }) {
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const names = useUserNames();
    const access = useCmAccess(programId);
    const drawer = useCmDrawerParams();
    const { user } = useAuth();
    const q = useOccurrenceDecisions(programId, {});
    // Same read, same cache entry as the Deliverables list's current period.
    const occs = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        includeProjected: true
    });
    const runner = useApprovalRunner(programId);
    const [sp] = useSearchParams();
    const [teamFilter, setTeamFilter] = useState('');
    // The Program Home waiver tiles link here with the period and status they count.
    const [statusFilter, setStatusFilter] = useState(()=>{
        const s = sp.get('waiverStatus') ?? '';
        return WAIVER_STEPS.some((o)=>o.value === s) ? s : '';
    });
    const [periodFilter, setPeriodFilter] = useState(()=>sp.get('waiverPeriod') ?? '');
    const [deliverableFilter, setDeliverableFilter] = useState('');
    const [sort, setSort] = useState(null);
    const [addingEvidence, setAddingEvidence] = useState(null);
    const [editing, setEditing] = useState(null);
    // SAD-1010: evidence added to an approved waiver is its own decision; it joins
    // the waiver's row here and never lists as a row of its own.
    const waiverEvidence = useMemo(()=>{
        const applied = new Map();
        const pending = new Set();
        // The latest approved waiver edit per date: its values are the waiver's now.
        const edited = new Map();
        const editPending = new Set();
        // SAD-1324: the same for an approved extension; evidence joins the latest one per date.
        const extApplied = new Map();
        const extPending = new Set();
        const latestExtension = new Map();
        for (const d of q.data?.decisions ?? []){
            if (d.kind === 'extension' && d.state === 'Applied') {
                const prev = latestExtension.get(d.occurrenceId);
                if (!prev || prev.decidedAt < d.decidedAt) latestExtension.set(d.occurrenceId, d);
                continue;
            }
            if (d.kind === 'extension_evidence') {
                if (d.state === 'Applied') {
                    extApplied.set(d.occurrenceId, [
                        ...extApplied.get(d.occurrenceId) ?? [],
                        ...d.attachments ?? []
                    ]);
                } else if (OPEN_STATES.has(d.state)) {
                    extPending.add(d.occurrenceId);
                }
                continue;
            }
            if (d.kind === 'waiver_edit') {
                if (d.state === 'Applied') {
                    const prev = edited.get(d.occurrenceId);
                    if (!prev || prev.decidedAt < d.decidedAt) edited.set(d.occurrenceId, d);
                } else if (OPEN_STATES.has(d.state)) {
                    editPending.add(d.occurrenceId);
                }
                continue;
            }
            if (d.kind !== 'waiver_evidence') continue;
            if (d.state === 'Applied') {
                applied.set(d.occurrenceId, [
                    ...applied.get(d.occurrenceId) ?? [],
                    ...d.attachments ?? []
                ]);
            } else if (OPEN_STATES.has(d.state)) {
                pending.add(d.occurrenceId);
            }
        }
        return {
            applied,
            pending,
            edited,
            editPending,
            extApplied,
            extPending,
            latestExtension
        };
    }, [
        q.data
    ]);
    const rows = useMemo(()=>{
        const occById = new Map((q.data?.occurrences ?? []).map((o)=>[
                o.id,
                o
            ]));
        const currentPeriodId = occs.data?.window?.periodOfPerformanceId;
        const currentOptionNumber = currentPeriodId ? lk.periodById.get(currentPeriodId)?.optionNumber : undefined;
        return toRows((q.data?.decisions ?? []).filter(isDateWaiver), occById, lk, user?.id, clins).map((r)=>({
                ...r,
                clinLabel: currentClinLabel(r.dutyKey, clins, currentOptionNumber) || (r.occ ? lk.exerciseById.get(r.occ.exerciseId)?.clinNumber ?? '' : '')
            }));
    }, [
        q.data,
        occs.data,
        lk,
        user?.id,
        clins
    ]);
    const shown = useMemo(()=>sortWaiverRows(rows.filter((r)=>(!teamFilter || r.teamId === teamFilter) && (!statusFilter || waiverStatusMatches(r.step, statusFilter)) && (!periodFilter || r.periodId === periodFilter) && (!deliverableFilter || r.title === deliverableFilter)), sort), [
        rows,
        teamFilter,
        statusFilter,
        periodFilter,
        deliverableFilter,
        sort
    ]);
    if (!q.isLoading && rows.length === 0) return null;
    const open = rows.filter((r)=>r.open);
    const awaiting = open.filter((r)=>r.step === 'awaiting_approval');
    const approvable = awaiting.filter((r)=>!r.mine);
    const canAct = access.canWrite;
    const teamOptions = [
        ...new Map(rows.map((r)=>[
                r.teamId,
                r.team
            ])).entries()
    ].map(([value, label])=>({
            value,
            label
        })).sort((a, b)=>a.label.localeCompare(b.label));
    const statusOptions = WAIVER_STEPS.filter((o)=>o.value === PENDING_STATUS || o.value === statusFilter || rows.some((r)=>r.step === o.value));
    const periodOptions = lk.periods.map((p)=>({
            value: p.id,
            label: p.name
        }));
    const deliverableOptions = uniqueSorted(rows.map((r)=>r.title)).map((t)=>({
            value: t,
            label: t
        }));
    const base = baseColumns(names, programId, clins);
    const byId = (id)=>base.find((c)=>c.id === id);
    const columns = [
        {
            id: 'title',
            header: 'Deliverable',
            sortable: true,
            headerStyle: pinned(TITLE_COL),
            // A truncating title would otherwise stretch the column past its pinned width.
            className: 'overflow-hidden',
            style: pinned(TITLE_COL),
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraEntityCell, {
                            title: r.title
                        }),
                        r.clinLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "info",
                            className: "tabular-nums",
                            "data-testid": Testids.cmApprovals.sourceClin,
                            children: r.clinLabel
                        }) : null
                    ]
                })
        },
        {
            ...byId('team'),
            headerStyle: pinned(TEAM_COL),
            style: pinned(TEAM_COL),
            // specra-allow arbitrary-length: a long team name wraps to two lines, not one word per line
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "block min-w-[12rem]",
                    children: r.team
                })
        },
        {
            id: 'due',
            header: 'Due',
            sortable: true,
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "whitespace-nowrap tabular-nums",
                    children: cmDate(r.occ?.dueOn, '—')
                })
        },
        byId('by'),
        {
            id: 'on',
            header: 'Proposed on',
            sortable: true,
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "whitespace-nowrap tabular-nums",
                    children: cmDate(instantDay(r.decision.proposedAt), '—')
                })
        },
        {
            id: 'corDate',
            header: 'COR date',
            align: 'right',
            sortable: true,
            cell: (r)=>/*#__PURE__*/ _jsx("span", {
                    className: "whitespace-nowrap tabular-nums",
                    children: r.step === 'proposed' ? 'awaiting the COR' : r.decision.corAgreedOn ? cmDate(r.decision.corAgreedOn) : 'date not recorded'
                })
        },
        {
            id: 'status',
            header: 'Status',
            sortable: true,
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: r.open ? 'warning' : 'success',
                    "data-testid": Testids.cmApprovals.step,
                    "data-step": r.step,
                    children: decisionStepLabel(r.decision) || (r.open ? 'Awaiting approval' : 'Approved')
                })
        },
        {
            id: 'reason',
            header: 'Note',
            cell: (r)=>{
                const why = decisionWhy(r.decision);
                // specra-allow arbitrary-length: long notes need a readable minimum width, no token fits
                return /*#__PURE__*/ _jsx("span", {
                    className: "block min-w-[18rem] text-muted-foreground",
                    children: why || '—'
                });
            }
        },
        {
            ...byId('cor'),
            header: 'COR Acceptance Reason',
            cell: (r)=>{
                const edit = r.decision.kind === 'waiver' && !r.open ? waiverEvidence.edited.get(r.decision.occurrenceId) : undefined;
                const answer = edit ? {
                    kind: 'waiver',
                    reason: edit.reason,
                    corAgreedOn: edit.waivedOn
                } : r.decision;
                const text = decisionNeedsNoCor(r.decision.kind) ? '—' : corWords(answer) || 'Not yet answered';
                return(// specra-allow arbitrary-length: the COR's reason is prose, it needs a readable minimum width
                /*#__PURE__*/ _jsxs("span", {
                    className: "block min-w-[18rem] text-muted-foreground",
                    children: [
                        text,
                        edit ? /*#__PURE__*/ _jsx("span", {
                            "data-testid": Testids.cmApprovals.waiverEdited,
                            children: ` (edited ${cmDate(instantDay(edit.decidedAt), '')})`
                        }) : null
                    ]
                }));
            }
        },
        {
            ...byId('evidence'),
            cell: (r)=>{
                const approvedWaiver = r.decision.kind === 'waiver' && !r.open;
                const approvedExtension = r.decision.kind === 'extension' && waiverEvidence.latestExtension.get(r.decision.occurrenceId)?.changeSetId === r.decision.changeSetId && !r.open;
                const added = approvedWaiver ? waiverEvidence.applied.get(r.decision.occurrenceId) : approvedExtension ? waiverEvidence.extApplied.get(r.decision.occurrenceId) : undefined;
                const edit = approvedWaiver ? waiverEvidence.edited.get(r.decision.occurrenceId) : undefined;
                const evidence = /*#__PURE__*/ _jsx(CmDecisionEvidence, {
                    programId: programId,
                    evidenceDocumentId: edit ? edit.waiverDocumentId : r.decision.evidenceDocumentId,
                    attachments: [
                        ...r.decision.attachments ?? [],
                        ...added ?? []
                    ],
                    testId: Testids.cmApprovals.evidence
                });
                return /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        evidence ?? /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: "—"
                        }),
                        approvedWaiver && waiverEvidence.pending.has(r.decision.occurrenceId) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            "data-testid": Testids.cmApprovals.waiverEvidencePending,
                            children: "Evidence awaiting approval"
                        }) : null,
                        approvedExtension && waiverEvidence.extPending.has(r.decision.occurrenceId) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            "data-testid": Testids.cmApprovals.extensionEvidencePending,
                            children: "Evidence awaiting approval"
                        }) : null,
                        approvedWaiver && waiverEvidence.editPending.has(r.decision.occurrenceId) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            "data-testid": Testids.cmApprovals.waiverEditPending,
                            children: "Edit awaiting approval"
                        }) : null
                    ]
                });
            }
        }
    ];
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": Testids.cmApprovals.dateWaivers,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: `Deliverable waivers and extensions · ${open.length - awaiting.length} awaiting the COR · ${awaiting.length} awaiting approval · ${rows.length - open.length} approved`,
                action: canAct && awaiting.length > 0 ? /*#__PURE__*/ _jsx(ApproveAll, {
                    approvable: approvable,
                    own: awaiting.length - approvable.length,
                    runner: runner
                }) : undefined
            }),
            /*#__PURE__*/ _jsx("div", {
                className: FILTER_ROW,
                children: [
                    [
                        'Deliverable',
                        deliverableFilter,
                        setDeliverableFilter,
                        'All deliverables',
                        deliverableOptions,
                        Testids.cmApprovals.waiverDeliverableFilter
                    ],
                    [
                        'Team',
                        teamFilter,
                        setTeamFilter,
                        'All teams',
                        teamOptions,
                        Testids.cmApprovals.waiverTeamFilter
                    ],
                    [
                        'Period',
                        periodFilter,
                        setPeriodFilter,
                        'All periods',
                        periodOptions,
                        Testids.cmApprovals.waiverPeriodFilter
                    ],
                    [
                        'Status',
                        statusFilter,
                        setStatusFilter,
                        'All statuses',
                        statusOptions,
                        Testids.cmApprovals.waiverStatusFilter
                    ]
                ].map(([label, value, set, all, options, testId])=>/*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": label,
                        className: label === 'Status' ? 'w-full sm:col-start-4' : label === 'Period' ? 'w-full sm:w-52' : COLUMN_SELECT,
                        value: value,
                        onChange: (e)=>set(e.target.value),
                        "data-testid": testId,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: all
                            }),
                            options.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.value,
                                    children: o.label
                                }, o.value))
                        ]
                    }, label))
            }),
            /*#__PURE__*/ _jsx(ApprovalFailures, {
                runner: runner
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: "Deliverable waivers and extensions",
                rows: shown,
                columns: columns.map((c)=>({
                        ...c,
                        headerClassName: 'whitespace-nowrap'
                    })),
                // Cap the height so the table scrolls in its own box (horizontal bar stays in view) and
                // pin every header cell, the actions one too, with an opaque background.
                tableProps: {
                    containerClassName: // specra-allow arbitrary-length: a viewport-relative cap, no spacing token fits
                    'max-h-[70vh] [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-background'
                },
                rowKey: (r)=>r.decision.changeSetId,
                rowTestId: Testids.cmApprovals.row,
                isLoading: q.isLoading || lk.isLoading,
                error: q.error,
                onRetry: ()=>void q.refetch(),
                onRowClick: (r)=>drawer.open('occ', r.decision.occurrenceId),
                getRowLabel: (r)=>`Open ${rowName(r)}`,
                sortColumn: sort?.key,
                sortDirection: sort ? toUiSortDir(sort.dir) : undefined,
                onSortChange: (columnId, direction)=>setSort({
                        key: columnId,
                        dir: fromUiSortDir(direction)
                    }),
                empty: /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    "data-testid": Testids.cmApprovals.waiverNoMatches,
                    children: "No waivers or extensions match these filters."
                }),
                rowActions: canAct ? (r)=>{
                    if (r.open) {
                        return /*#__PURE__*/ _jsx(DecisionActions, {
                            r: r,
                            runner: runner,
                            onRecordCor: ()=>drawer.open('occ', r.decision.occurrenceId)
                        });
                    }
                    const occId = r.decision.occurrenceId;
                    if (r.decision.kind === 'extension') {
                        if (waiverEvidence.latestExtension.get(occId)?.changeSetId !== r.decision.changeSetId) return null;
                        const extPending = waiverEvidence.extPending.has(occId);
                        const add = /*#__PURE__*/ _jsx("div", {
                            className: "flex justify-end gap-2",
                            children: /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                disabled: extPending,
                                onClick: ()=>setAddingEvidence(r),
                                "data-testid": Testids.cmApprovals.addExtensionEvidence,
                                children: "Add evidence"
                            })
                        });
                        return extPending ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: "A change to this extension is already awaiting a second person’s approval. Only one change can be open per date.",
                            children: /*#__PURE__*/ _jsx("span", {
                                children: add
                            })
                        }) : add;
                    }
                    if (r.decision.kind !== 'waiver') return null;
                    const pending = waiverEvidence.pending.has(occId) || waiverEvidence.editPending.has(occId);
                    const buttons = /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                disabled: pending,
                                onClick: ()=>setAddingEvidence(r),
                                "data-testid": Testids.cmApprovals.addWaiverEvidence,
                                children: "Add evidence"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                disabled: pending || !r.occ,
                                onClick: ()=>setEditing(r),
                                "data-testid": Testids.cmApprovals.editWaiver,
                                children: "Edit"
                            })
                        ]
                    });
                    return pending ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                        content: "A change to this waiver is already awaiting a second person’s approval. Only one change can be open per date.",
                        children: /*#__PURE__*/ _jsx("span", {
                            children: buttons
                        })
                    }) : buttons;
                } : undefined
            }),
            addingEvidence ? /*#__PURE__*/ _jsx(EvidenceDialog, {
                programId: programId,
                row: addingEvidence,
                onClose: ()=>setAddingEvidence(null)
            }) : null,
            editing?.occ ? /*#__PURE__*/ _jsx(WaiverEditDialog, {
                programId: programId,
                row: editing,
                current: {
                    waivedOn: editing.occ.waivedOn,
                    reason: editing.occ.waiverReason,
                    waiverDocumentId: editing.occ.waiverDocumentId
                },
                onClose: ()=>setEditing(null)
            }) : null
        ]
    });
}
